import { useState, useEffect } from "react";
import { Mail, MapPin, Send, Facebook, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

import { API } from "../config/api";

export default function ContactPage() {
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/site-pages/contact`)
      .then(res => res.json())
      .then(data => {
        if (!data.not_found) setPageData(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Message sent! We'll get back to you soon.");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get section by type
  const getSection = (type) => pageData?.sections?.find(s => s.type === type);

  const heroSection = getSection("hero") || {
    heading: "Contact Us",
    subheading: "Get In Touch",
    body: "Have questions about our events or want to get involved? We'd love to hear from you!"
  };

  const infoSection = getSection("info") || {
    heading: "Reach Out to Us",
    items: [
      { icon: "Mail", label: "Email", value: "tatvamasilondonontario@gmail.com", href: "mailto:tatvamasilondonontario@gmail.com" },
      { icon: "MapPin", label: "Location", value: "London, Ontario, Canada", href: "" }
    ]
  };

  const socialSection = getSection("social") || {
    items: [
      { platform: "facebook", url: "https://www.facebook.com/share/17tzecSnkU/" },
      { platform: "instagram", url: "https://www.instagram.com/tatvamasilondonontario?igsh=NzZ3cXIzdHV1andj" }
    ]
  };

  const ICON_MAP = { Mail, MapPin };

  return (
    <div className="pt-20 bg-kerala-cream" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-kerala-green">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">{heroSection.subheading}</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white mb-6">
            {heroSection.heading}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {heroSection.body}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl text-kerala-green mb-8">
                {infoSection.heading}
              </h2>

              <div className="space-y-6 mb-10">
                {infoSection.items?.map((info, idx) => {
                  const IconComponent = ICON_MAP[info.icon] || Mail;
                  return (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center shrink-0">
                        <IconComponent className="w-5 h-5 text-kerala-green" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{info.label}</p>
                        {info.href ? (
                          <a 
                            href={info.href}
                            className="text-kerala-green hover:text-kerala-terracotta font-medium transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-kerala-green font-medium">{info.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Social Links */}
              <div>
                <p className="text-sm text-gray-500 mb-4">Follow Us</p>
                <div className="flex gap-4">
                  {socialSection.items?.map((social, idx) => {
                    const Icon = social.platform === "facebook" ? Facebook : Instagram;
                    return (
                      <a
                        key={idx}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-kerala-green hover:bg-kerala-terracotta rounded-full flex items-center justify-center transition-colors"
                        data-testid={`contact-${social.platform}`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-kerala-gold/20 rounded-sm p-8 shadow-lg">
              <h3 className="font-heading text-2xl text-kerala-green mb-6">
                Send Us a Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="mt-1"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
